import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { bookmarkAPI, getErrorMessage } from '../lib/api';
import { 
  Bookmark, Play, Trash2, Search, 
  BookOpen, Calendar, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { renderLatex } from '../lib/renderLatex';

const Bookmarks = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    loadBookmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const loadBookmarks = async () => {
    try {
      setLoading(true);
      const response = await bookmarkAPI.getAll();
      // Handle both array response and wrapped response
      const data = Array.isArray(response.data) ? response.data : (response.data?.bookmarks || response.data || []);
      setBookmarks(Array.isArray(data) ? data : []);
    } catch (error) {
      // If unauthorized, redirect to auth
      if (error.response?.status === 401) {
        navigate('/auth');
        return;
      }
      toast.error('Failed to load bookmarks');
      setBookmarks([]);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (questionId) => {
    try {
      await bookmarkAPI.remove(questionId);
      setBookmarks(bookmarks.filter(b => b.question_id !== questionId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  const generateTest = async () => {
    if (bookmarks.length === 0) {
      toast.error('No bookmarks to create test from');
      return;
    }

    try {
      setGenerating(true);
      const response = await bookmarkAPI.generateTest();
      toast.success(`Created test with ${response.data.question_count} questions`);
      navigate(`/test/${response.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate test'));
    } finally {
      setGenerating(false);
    }
  };

  // Get unique subjects for filter
  const subjects = [...new Set(bookmarks.map(b => b.question?.subject || 'Unknown'))];

  // Filter bookmarks
  const filteredBookmarks = bookmarks.filter(b => {
    const matchesSearch = searchQuery === '' || 
      b.question?.question_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.question?.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.question?.chapter?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSubject = filterSubject === 'all' || b.question?.subject === filterSubject;
    
    return matchesSearch && matchesSubject;
  });

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="lg" text="Loading bookmarks..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Bookmark className="text-blue-500" size={32} />
              My Bookmarks
            </h1>
            <p className="text-muted-foreground mt-1">
              {bookmarks.length} bookmarked question{bookmarks.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <Button
            onClick={generateTest}
            disabled={bookmarks.length === 0 || generating}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
          >
            <Play size={18} className="mr-2" />
            {generating ? 'Creating...' : 'Practice All Bookmarks'}
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search bookmarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="relative">
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="appearance-none bg-background border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 pr-10 text-sm"
              >
                <option value="all">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
            </div>
          </div>
        </Card>

        {/* Bookmarks List */}
        {filteredBookmarks.length === 0 ? (
          <Card className="p-12 text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <Bookmark size={48} className="mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Bookmarks Yet</h3>
            <p className="text-muted-foreground mb-6">
              Bookmark questions while practicing to review them later
            </p>
            <Button onClick={() => navigate('/exams')} variant="outline">
              Start Practicing
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookmarks.map((bookmark) => (
              <Card 
                key={bookmark.id}
                className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                <div className="flex gap-4">
                  <div className="flex-1">
                    {/* Meta */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {bookmark.question?.exam && (
                        <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                          {bookmark.question.exam}
                        </span>
                      )}
                      {bookmark.question?.subject && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700">
                          <BookOpen size={12} className="inline mr-1" />
                          {bookmark.question.subject}
                        </span>
                      )}
                      {bookmark.question?.chapter && (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {bookmark.question.chapter}
                        </span>
                      )}
                      {bookmark.question?.year && (
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                          <Calendar size={12} className="inline mr-1" />
                          {bookmark.question.year}
                        </span>
                      )}
                    </div>

                    {/* Question Text */}
                    <div 
                      className="text-foreground line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: renderLatex(bookmark.question?.question_text || 'Question text unavailable') }}
                    />

                    {/* Bookmark Date */}
                    <p className="text-xs text-muted-foreground mt-3">
                      Bookmarked {new Date(bookmark.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBookmark(bookmark.question_id)}
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Remove bookmark"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Bookmarks;
